import {
	find,
	findIndex
} from "lodash";
import template from "./degree-minute.html";
import {
	EVENT_FIELD_DISABLE,
	EVENT_FIELD_ENABLE,
	EVENT_SET_VALUE,
	EVENT_CLEAR_VALUE,
	EVENT_UNCLEAR_VALUE,
	EVENT_SET_FEATURE_DATA,
	FEATURE_TYPES,
	EVENT_SEND_VALUE,
	EVENT_GET_VALUE,
	EVENT_REPORT_LOCKED,
	EVENT_REVERT_VALUES,
	EVENT_SET_FORM_PRISTINE
} from "constants.js";
import {
	degreesToDms,
	dmsToDegrees
} from "../../../../services/geo-service";

export class Controller {
	currentValue = { unit: {} };
	fieldControllers = [];
	lookups = [];

	constructor($scope, $logger, eventBus, featureHelper) {
		this._scope = $scope;
		this.$logger = $logger;
		// eslint-disable-next-line no-useless-escape
		this.isNumber = /^(\-)?(\d*(\.|\.\d+)?)?$/;
		this.featureHelper = featureHelper;
	}

	$onInit() {
		this._eventBus = this.reportScope.events;
		this._unregister = this.reportFormCtrl.registerFieldController(this);

		this.field.options = this.field.Data;
		this.currentValue.unit = find(this.field.options, (unit) => unit.Code === "°");

		this.setValue();
		this.setFeatures();

		this.setPristine();

		const self = this;
		this._eventBus.register(EVENT_SET_VALUE(this.field.Id), (e, params) => self.setValue(params.value, true), this._scope);

		this._eventBus.register(EVENT_CLEAR_VALUE(this.field.Id), (e, params) => {
			if(!self.cleared && (!!self.currentValue?.degrees) && params.isInitializing !== true && params.doNotSave !== true) {
				self.cleared = { ...self.currentValue };
			}
			self.currentValue = {
				...self.currentValue,
				degrees: null,
				minutes: null,
				roundedMinutes: null
			};
		}, this._scope);

		this._eventBus.register(EVENT_UNCLEAR_VALUE(this.field.Id), () => {
			if(!!self.cleared) {
				self.currentValue = this.cleared;
				self.cleared = undefined;
			}
		}, this._scope);

		this._eventBus.register(EVENT_GET_VALUE(this.field.Id), (_, params) => {
			this._eventBus.trigger(EVENT_SEND_VALUE(params.id), {
				key: this.field.Key.Code,
				value: this.getCurrentValue()
			});
		}, this._scope);

		this._eventBus.register(EVENT_FIELD_DISABLE(this.field.Id), () => self.disableField(), this._scope);
		this._eventBus.register(EVENT_FIELD_ENABLE(this.field.Id), () => self.enableField(), this._scope);
		this._eventBus.register(EVENT_REPORT_LOCKED, () => self.field.isReadonly = true, this._scope);
		this._eventBus.register(EVENT_REVERT_VALUES, () => {
			this.revertValue();
		}, this._scope);

		this._eventBus.register(EVENT_SET_FORM_PRISTINE, () => {
			this.setPristine();
		}, this._scope);

		const mapFeature = find(this.field.features, { Type: FEATURE_TYPES.mapPoint });
		if(!!this.initialValue || (mapFeature && mapFeature.geoJsons !== undefined)) {
			this.lookups.forEach(l => l.disabled = false);
		}
		else {
			this.lookups.forEach(l => l.disabled = true);
		}

		this._scope.$watch(
			() => self.currentValue.isNegative,
			() => self.setCompassDirection()
		);
	}

	enableField() {
		this.field.isDisabled = false;
		this.field.placeHolder = null;
		this.lookups.forEach(l => l.disabled = false);
	}

	disableField() {
		this.field.isDisabled = true;
		if(!!this.currentValue) {
			this.lookups.forEach(l => l.disabled = false);
		}
		else {
			this.lookups.forEach(l => l.disabled = true);
		}
	}

	isDirty(result) {
		return (this.field.visible !== false || this.initialValue !== undefined)
			&& this.field.IsNotReported !== true
			&& this.field.IsHidden !== true
			&& (result != this.initialValue?.Number);
	}

	triggerDirtyEvent() {
		const val = this.buildResult();
		const isDirty = this.isDirty(val?.Number);

		this.setFieldDirty(this.field.Id, isDirty);
	}

	onNumberChanged(newValue, oldValue, propName) {
		if(newValue === "") {
			this.currentValue[propName] = "";
			return;
		}

		return newValue.match(this.isNumber) ? newValue : oldValue;
	}

	handleKeyUp(event) {
		const regex = /[0-9.-\\t]/;
		const isValid = regex.test(event.key);

		if(!isValid) {
			event.preventDefault();
		}
	}

	handleBlur() {
		this.forcePositive();

		const fullMinutes = this.currentValue.roundedMinutes;

		this.currentValue.minutes = Math.round(fullMinutes);
		this.currentValue.roundedMinutes = this.currentValue.minutes;
	}

	setCompassDirection() {
		if(this.field.Key.Code === "TRIP.AREAS.LATITUDE.SINGLE") {
			this.compassLetter = this.currentValue.isNegative ? "S" : "N";
		}
		else {
			this.compassLetter = this.currentValue.isNegative ? "W" : "E";
		}

		this.triggerDirtyEvent();
	}

	swapCompass() {
		this.currentValue.isNegative = !this.currentValue.isNegative;
		this.setCompassDirection();
	}

	getCurrentValue = (isApiCall) => {
		let result = this.buildResult();

		if(!isApiCall) {
			if(!result) {
				result = { FieldId: this.field.Id };
				result.hasResultForRuleRun = false;
			}
			result.factor = 1;
			result.isDirty = this.isDirty(result.Number);
		}

		return result ? [result] : [];
	}

	revertValue() {
		const dms = degreesToDms(this.initialValue.Number);

		this.currentValue.degrees = dms.degrees;
		this.currentValue.minutes = this.currentValue.roundedMinutes = dms.minutes;
	}

	setPristine() {
		const newInitial = this.field.IsDetail ? undefined : this.buildResult();
		this.initialValue = newInitial;
	}

	buildResult() {
		let result = undefined;
		if(this.currentValue.unit && this.currentValue.unit.Id) {
			const fullDegrees = dmsToDegrees(this.currentValue);

			if(fullDegrees) {
				result = {
					FieldId: this.field.Id,
					Number: fullDegrees,
					DataId: this.currentValue.unit.Id
				};
			}
		}
		return result;
	}

	setValue(value, fromEvent = false) {
		this.value = value ? value : find(this.values, v => v.FieldId === this.field.Id);

		if(this.value) {
			this.currentValue = degreesToDms(this.value.Number, fromEvent);
			this.currentValue.unit = find(this.field.options, (unit) => unit.Code === "°");

			if(!this.currentValue.unit && this.field.Data.length === 1) {
				this.currentValue.unit = this.field.Data[0];
			}
		}
		else {
			this.currentValue.unit = find(this.field.options, (unit) => unit.Code === "°");
			if(this.field.Key.Code === "TRIP.AREAS.LONGITUDE.SINGLE")
				this.currentValue.isNegative = true;
		}

		if(this.currentValue.unit === undefined) {
			this.field.isReadonly = true;
			this.$logger.warn("Degree minute field unit not found!");
		}

		this.setCompassDirection();
	}

	$onDestroy() {
		this._unregister();
	}

	registerFieldController = (fieldController) => {
		this.fieldControllers.push(fieldController);
		return function unregister() {
			const idx = findIndex(this.fieldControllers, x => x === fieldController);
			if(idx !== -1) {
				this.fieldControllers.splice(idx, 1);
			}
		};
	}

	setFeatures() {
		const mapFeature = find(this.field.features, { Type: FEATURE_TYPES.mapPoint });

		if(mapFeature) {
			this.lookups.push({
				name: mapFeature.Type,
				buoys: mapFeature.buoys,
				fieldId: this.field.Id,
				disabled: false,
				data: mapFeature.geoJsons,
				valueFields: mapFeature.locationFields,
				fields: !!mapFeature.geoJsons && mapFeature.locationFields,
				isUpdatable: () => !this.field.isReadonly && !this.field.isDisabled,
				degreeMapper: (rawDegrees, isMapRequest) => degreesToDms(rawDegrees, isMapRequest)
			});

			this._eventBus.register(EVENT_SET_FEATURE_DATA(mapFeature.Type), (e, params) => {
				const lookup = find(this.lookups, l => l.name === mapFeature.Type);
				lookup.data = params.geoJsons;
				lookup.fields = params.locationFields;
			}, this._scope);
		}
	}

	getValidationStyling() {
		return this.field.hasValidationError ? "validation-error"
			: this.field.hasValidationWarning ? "validation-warning" : "";
	}

	getCoordinates() {
		return `${this.currentValue.degrees}° ${this.currentValue.minutes}'`;
	}
}

export default angular
	.module("vesl.components.report-form.degree-minute", [])
	.component("degreeMinute", {
		template,
		controller: Controller,
		bindings: {
			field: "<",
			values: "<",
			setFieldDirty: "="
		},
		require: {
			reportFormCtrl: "^reportForm",
			reportScope: "^^reportScope"
		}
	}).name;
